import { Immutable } from 'immer';
import { msg } from '@lingui/core/macro';
import { AtomicUnit, Unit } from './types';
import { getAtomicUnitBang } from './registry';
import { MessageDescriptor } from '@lingui/core';
import { i18n } from '@watershed/intl/i18n';

/**
 * A real Unit has the string key cast as an AtomicUnit after verifying that it
 * exists in the unit registry. In some transforms, though, we want the freedom
 * to replace that AtomicUnit string with an arbitrary string that we've judged
 * optimal for human readers (e.g. "kilowatt-hour" to "kWh"). So we use a looser
 * unit type.
 */
type LooseUnit = Map<string, number>;

/**
 * Maps a **canonical** AtomicUnit to the form we'd like to render in its place.
 * (Traces represent all AtomicUnits in their canonical form, so that's what
 * we'll be seeing & wanting to transform.)
 *
 * We make judgments about what to put here based on criteria like these:
 *
 * - What are readers likely to see elsewhere? E.g. "kgCO₂e" is what
 *   publications will have, not "kgco2e".
 * - Will legibility be assisted by a universally known abbreviation? E.g. we
 *   assume every reader knows "km" is "kilometer", so would rather see that in
 *   the rendering because it takes up less space, seems less noisy.
 * - Is it unambiguous? If an abbreviation might stand for multiple things, we
 *   do not use it, and instead render the full name.
 */
const unitStringToFormattedUnitString = new Map<string, MessageDescriptor>([
  [getAtomicUnitBang('foot'), msg({ message: 'ft', comment: 'Feet unit' })],
  [
    getAtomicUnitBang('kgco2e'),
    msg({ message: 'kgCO₂e', comment: 'Kilograms of carbon dioxide unit' }),
  ],
  [
    getAtomicUnitBang('kilogram'),
    msg({ message: 'kg', comment: 'Kilograms unit' }),
  ],
  [
    getAtomicUnitBang('kilometer'),
    msg({ message: 'km', comment: 'Kilometer unit' }),
  ],
  [getAtomicUnitBang('meter'), msg({ message: 'm', comment: 'Meter unit' })],
  [
    getAtomicUnitBang('mmbtu'),
    msg({ message: 'MMBtu', comment: 'Metric million British thermal unit' }),
  ],
  [getAtomicUnitBang('pound'), msg({ message: 'lb', comment: 'Pound unit' })],
  [
    getAtomicUnitBang('tonco2e'),
    msg({
      message: 'tCO₂e',
      comment: 'Tonnes of carbon dioxide equivalent unit',
    }),
  ],
  [
    getAtomicUnitBang('tonneco2e'),
    msg({
      message: 'tCO₂e',
      comment: 'Tonnes of carbon dioxide equivalent unit',
    }),
  ],
  [
    getAtomicUnitBang('kilowatt-hour'),
    msg({ message: 'kWh', comment: 'Kilowatt hour unit' }),
  ],
  [
    getAtomicUnitBang('megawatt-hour'),
    msg({ message: 'MWh', comment: 'Megawatt hour unit' }),
  ],
  [
    getAtomicUnitBang('mwh'),
    msg({ message: 'MWh', comment: 'Megawatt hour unit' }),
  ],
  [
    getAtomicUnitBang('square_feet'),
    msg({ message: 'square feet', comment: 'Square feet unit' }),
  ],
  [
    getAtomicUnitBang('instance_hour'),
    msg({ message: 'instance hour', comment: 'Instance hour unit' }),
  ],
]);

function applyAtomicUnitToFormattedUnit(unit: Immutable<LooseUnit>): LooseUnit {
  const result: LooseUnit = new Map();
  for (const [name, exponent] of unit) {
    const mapped = unitStringToFormattedUnitString.get(name);
    if (mapped) {
      result.set(i18n.t(mapped), exponent);
    } else {
      const tidied = name.replaceAll('_', ' ');
      result.set(tidied, exponent);
    }
  }
  return result;
}

/**
 * Maps a multiplicative unit pair to a string that should replace it.
 *
 * These replacements take for granted that the input unit is well formed: for
 * example, the same unit cannot include *both* kWh and MWh as subunits, because
 * in a well-formed unit those would be combined.
 */
const multUnitToFormattedUnit = new Map<
  [AtomicUnit, AtomicUnit],
  MessageDescriptor
>([
  [[getAtomicUnitBang('kilowatt'), getAtomicUnitBang('hour')], msg`kWh`],
  [[getAtomicUnitBang('megawatt'), getAtomicUnitBang('hour')], msg`MWh`],
]);

function applyMultUnitToFormattedUnit(unit: Immutable<LooseUnit>): LooseUnit {
  const result: LooseUnit = new Map();

  // This is not the most efficient possible way to perform the transform, but
  // it does the job and, importantly, it *preserves the order* from the input
  // unit. Units are small maps, so the inefficiency should not matter.
  for (const [name, exponent] of unit) {
    let entryHasBeenReformatted = false;
    for (const [mult, replacement] of multUnitToFormattedUnit) {
      const hasMult =
        unit.has(mult[0]) && unit.get(mult[0]) === unit.get(mult[1]);
      if (hasMult && name === mult[0]) {
        result.set(i18n.t(replacement), exponent);
        entryHasBeenReformatted = true;
        break;
      }
      if (hasMult && name === mult[1]) {
        entryHasBeenReformatted = true;
        break;
      }
    }
    if (!entryHasBeenReformatted) {
      result.set(name, exponent);
    }
  }

  return result;
}

/**
 * A catch-all function for any transforms we couldn't apply in the maps above.
 */
function applyArbitraryUnitTransforms(unit: Immutable<LooseUnit>): LooseUnit {
  const result: LooseUnit = new Map();

  for (const [name, exponent] of unit) {
    switch (name) {
      case 'square_foot':
        result.set('ft', exponent * 2);
        break;
      case 'cubic_foot':
        result.set('ft', exponent * 3);
        break;
      case 'square_meter':
        result.set('m', exponent * 2);
        break;
      default:
        result.set(name, exponent);
    }
  }

  return result;
}

/**
 * Returns a reformatted version of the input unit.
 */
export function reformatUnit(unit: Unit): Map<string, number> {
  let result = applyArbitraryUnitTransforms(unit);
  result = applyAtomicUnitToFormattedUnit(result);
  result = applyMultUnitToFormattedUnit(result);
  return result;
}
